import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

export default function AlertSendAnimation({ width = 260 }) {
  const height = width * 0.9;

  const [cardStage, setCardStage] = useState("visible");
  const [showEnvelope, setShowEnvelope] = useState(false);
  const [flapClosed, setFlapClosed] = useState(false);
  const [floatAway, setFloatAway] = useState(false);

  useEffect(() => {
    const shrinkDelay = setTimeout(() => setCardStage("shrink"), 3500);
    const envelopeDelay = setTimeout(() => setShowEnvelope(true), 3800);
    const flapDelay = setTimeout(() => setFlapClosed(true), 4600);
    const floatDelay = setTimeout(() => setFloatAway(true), 5400);

    return () => {
      clearTimeout(shrinkDelay);
      clearTimeout(envelopeDelay);
      clearTimeout(flapDelay);
      clearTimeout(floatDelay);
    };
  }, []);

  const cardAnimation =
    cardStage === "visible"
      ? { scale: 1, y: 0, opacity: 1 }
      : { scale: 0.68, y: -20, opacity: 0 };

  const cardTransition = {
    duration: 0.9,
    ease: "easeInOut"
  };

  return (
    <div
      style={{
        width,
        height,
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      <AnimatePresence>
        {cardStage && (
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={cardAnimation}
            exit={{ opacity: 0 }}
            transition={cardTransition}
            style={{
              width,
              height,
              position: "absolute",
              borderRadius: 16,
              background: "linear-gradient(180deg,#ede6ff,#dceaff)",
              boxShadow: "0 8px 18px rgba(0,0,0,0.08)",
              padding: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              zIndex: 2
            }}
          >

            {/* Header */}
            <div
              style={{
                width: "100%",
                padding: "10px 14px",
                background: "linear-gradient(90deg,#cfc2ff,#d9e4ff)",
                fontSize: 13,
                fontWeight: 600,
                color: "#3d366b"
              }}
            >
              Alert for Dr. John S
            </div>

            {/* Main content wrapper */}
            <div
              style={{
                flex: 1,
                padding: 16,
                display: "flex",
                flexDirection: "column",
                gap: 14,
                justifyContent: "flex-start"
              }}
            >

              {/* Chart row */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%"
                }}
              >
                {/* Bars */}
                <div style={{ flex: 1, marginRight: 12 }}>
                  <div style={{ marginBottom: 6 }}>
                    <div
                      style={{
                        fontSize: 10,
                        color: "#7a7a95",
                        marginBottom: 2
                      }}
                    >
                      Observed
                    </div>

                    <motion.div
                      initial={{ width: "0%" }}
                      animate={{ width: "65%" }}
                      transition={{ duration: 1.2 }}
                      style={{
                        height: 10,
                        background: "linear-gradient(90deg,#7c3aed,#3b82f6)",
                        borderRadius: 6
                      }}
                    />
                  </div>

                  <div>
                    <div
                      style={{
                        fontSize: 10,
                        color: "#7a7a95",
                        marginBottom: 2
                      }}
                    >
                      Expected milestone
                    </div>

                    <motion.div
                      initial={{ width: "0%" }}
                      animate={{ width: "40%" }}
                      transition={{ duration: 1.2, delay: 0.2 }}
                      style={{
                        height: 10,
                        background: "linear-gradient(90deg,#c5c6e8,#e1e2f5)",
                        borderRadius: 6
                      }}
                    />
                  </div>
                </div>

                {/* Ring chart */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center"
                  }}
                >
                  <motion.svg
                    width={42}
                    height={42}
                    viewBox="0 0 38 38"
                    initial={{ rotate: -90 }}
                    animate={{ rotate: 0 }}
                    transition={{ duration: 0.8 }}
                  >
                    <circle
                      cx="19"
                      cy="19"
                      r="16"
                      stroke="rgba(180,180,230,0.3)"
                      strokeWidth="5"
                      fill="none"
                    />

                    <motion.circle
                      cx="19"
                      cy="19"
                      r="16"
                      stroke="url(#grad)"
                      strokeWidth="5"
                      fill="none"
                      strokeLinecap="round"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 0.45 }}
                      transition={{ duration: 1.2 }}
                    />

                    <defs>
                      <linearGradient id="grad" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#7c3aed" />
                        <stop offset="100%" stopColor="#3b82f6" />
                      </linearGradient>
                    </defs>
                  </motion.svg>

                  <div
                    style={{
                      fontSize: 11,
                      color: "#6b6b8a",
                      marginTop: 4
                    }}
                  >
                    Side-effects
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div
                style={{
                  marginTop: 4,
                  padding: "10px 12px",
                  borderRadius: 12,
                  background: "linear-gradient(180deg,#f5f3ff,#edf2ff)",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                  fontSize: 12,
                  color: "#4c4c72",
                  lineHeight: 1.45
                }}
              >
                Alice’s GAD-7 scores are 30% below the expected 3-week mark.
              </div>

            </div>

            {/* Buttons pinned to bottom */}
            <div
              style={{
                display: "flex",
                width: "100%"
              }}
            >
              <button
                style={{
                  flex: 1,
                  padding: "10px 0",
                  border: "none",
                  background: "linear-gradient(90deg,#7c3aed,#3b82f6)",
                  color: "white",
                  fontSize: 13,
                  cursor: "pointer"
                }}
              >
                View Full Summary
              </button>

              <button
                style={{
                  flex: 1,
                  padding: "10px 0",
                  border: "none",
                  background: "rgba(124,58,237,0.12)",
                  color: "#4a4a7a",
                  fontSize: 13,
                  cursor: "pointer"
                }}
              >
                Reschedule Visit
              </button>
            </div>

          </motion.div>
        )}
      </AnimatePresence>

      {/* Envelope */}
      {showEnvelope && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{
            opacity: 1,
            scale: 1,
            y: floatAway ? -120 : 0
          }}
          transition={{
            duration: floatAway ? 1.4 : 0.6,
            ease: floatAway ? "easeOut" : "easeInOut"
          }}
          style={{
            width,
            height,
            position: "absolute",
            zIndex: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <EnvelopeSVG width={width} flapClosed={flapClosed} />
        </motion.div>
      )}
    </div>
  );
}

function EnvelopeSVG({ width, flapClosed }) {
  const h = width * 0.75;

  return (
    <svg width={width} height={h} viewBox={`0 0 ${width} ${h}`}>
      <rect
        x="0"
        y={h * 0.22}
        width={width}
        height={h * 0.62}
        rx={14}
        fill="url(#envBody)"
      />

      <motion.path
        d={`M 0 ${h * 0.22} L ${width / 2} 0 L ${width} ${h * 0.22} Z`}
        fill="url(#envFlap)"
        animate={{
          rotateX: flapClosed ? 0 : -85,
          transformOrigin: `50% ${h * 0.22}px`
        }}
        transition={{ duration: 0.6, ease: "easeInOut" }}
      />

      <path
        d={`M 0 ${h * 0.22} L ${width * 0.5} ${h * 0.5} L 0 ${h * 0.84} Z`}
        fill="rgba(255,255,255,0.2)"
      />

      <path
        d={`M ${width} ${h * 0.22} L ${width * 0.5} ${h * 0.5} L ${width} ${h * 0.84} Z`}
        fill="rgba(255,255,255,0.2)"
      />

      <defs>
        <linearGradient id="envBody" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(210,210,255,0.9)" />
          <stop offset="100%" stopColor="rgba(190,210,255,0.9)" />
        </linearGradient>

        <linearGradient id="envFlap" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(225,215,255,0.95)" />
          <stop offset="100%" stopColor="rgba(200,210,255,0.9)" />
        </linearGradient>
      </defs>
    </svg>
  );
}
